import React, { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { sortingFns } from "@tanstack/react-table";
import { compareItems } from "@tanstack/match-sorter-utils";

import MasterTable from "../../../components/master/MasterTable";

import { contractAddIcon, downloadIcon } from "../../../assetData/icons";
import ComponentLoader from "../../../components/ui/pageLoader/ComponentLoader";
import { actionPerm } from "../../../components/authNavigation/AuthNavbar";
import { sortData } from "../../../utils/sortData";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { downloadLoader } from "../../../assetData/loaders";

const fuzzySort = (rowA, rowB, columnId) => {
  console.log("functioning... fuzzySort");
  let dir = 0;

  // Only sort by rank if the column has ranking information
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank,
      rowB.columnFiltersMeta[columnId]?.itemRank,
    );
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

const MasterLedgerTable = (props) => {
  const {
    tableData,
    buttonConfig,
    setIsAdd,
    setIsEdit,
    setIsDelete,
    setSelectedItemId,
    renderKey,
    setGlobalFilters,
    loading,
    actionPermissions,
    name,
    isDownloadClicked,
  } = props;

  const [tableFilter, setTableFilter] = useState(tableData);
  const [sortingType, setSortingType] = useState("asc");
  const [sortColumn, setSortColumn] = useState("account_name");

  useEffect(() => {
    setTableFilter(tableData);
  }, [tableData]);

  const handleSorting = (column, sortType, type) => {
    setSortColumn(column);

    const sortedData = sortData({
      data: tableData,
      column,
      type,
      sortType,
    });

    setTableFilter(sortedData);
    setSortingType(sortingType === "asc" ? "desc" : "asc");
  };

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "sl",
        header: () => <span>S. NO.</span>,
        cell: (info) => <span>{info.row.index + 1}.</span>,
      },

      {
        accessorFn: (row) => row.account_name,
        id: "account_name",
        cell: (info) => info.getValue(),
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "account_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between"
          >
            <div>LEDGER NAME</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "account_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "account_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        filterFn: "fuzzy",
        sortingFn: fuzzySort,
      },

      {
        accessorKey: "account_short_name",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "account_short_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between"
          >
            <div>SHORT NAME</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "account_short_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "account_short_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <span> {info.getValue()} </span>,
      },

      {
        accessorKey: "group_name",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "group_name",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between"
          >
            <div>GROUP NAME</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "group_name" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "group_name" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <span> {info.getValue()} </span>,
      },

      {
        accessorKey: "account_type",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "account_type",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between"
          >
            <div>ACCOUNT TYPE</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "account_type" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "account_type" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <span> {info.getValue()}</span>,
      },

      {
        accessorKey: "credit_debit",
        header: () => (
          <div
            onClick={() => {
              handleSorting(
                "credit_debit",
                sortingType === "asc" ? "desc" : "asc",
                "string",
              );
            }}
            className=" flex justify-between"
          >
            <div>Cr / Dr</div>
            <div className="space-y-[-10px]">
              <FaSortUp
                className={` ${
                  sortColumn === "credit_debit" && sortingType === "asc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
              <FaSortDown
                className={`${
                  sortColumn === "credit_debit" && sortingType === "desc"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              />
            </div>
          </div>
        ),
        cell: (info) => <span> {info.getValue()} </span>,
      },

      // {
      //   accessorKey: "macro_economic_sector",
      //   header: () => <span>INTERNAL</span>,
      //   cell: (info) => <span> {info.getValue()} </span>,
      // },

      {
        accessorKey: "status",
        header: () => <span>Status</span>,
        cell: (info) => <span> {info.getValue()} </span>,
      },

      // {
      //   accessorKey: "updated_at",
      //   header: () => <span>last updated on</span>,
      //   cell: (info) => (
      //     <span>
      //       {new Date(info.getValue()).toLocaleString("en-US", {
      //         day: "2-digit",
      //         month: "short",
      //         year: "numeric",
      //         hour: "numeric",
      //         minute: "numeric",
      //         hour24: true,
      //       })}
      //     </span>
      //   ),
      // },

      // {
      //   accessorKey: "eff_start_date",
      //   header: () => <span>START DATE</span>,
      //   cell: (info) =>
      //     info.getValue() && format(new Date(info.getValue()), "dd MMM yyyy"),
      // },

      // {
      //   accessorKey: "updated_at",
      //   header: () => <span>last updated by</span>,
      //   cell: (info) =>
      //     info.getValue() && format(parseISO(info.getValue()), "dd MMM yyyy"),
      // },

      // {
      //   accessorKey: "updated_by",
      //   header: () => <span>last UPDATED BY</span>,
      //   cell: (info) => <span> {info.getValue()} </span>,
      // },

      {
        accessorKey: "id",
        header: () => <span>Actions</span>,
        cell: (info) => <span> </span>,
      },
    ],
    [sortColumn, sortingType, tableData, tableFilter],
  );

  const actionConfig = [
    {
      id: "1",
      name: "Add Account",
      image: contractAddIcon,
      actionHandler: () => setIsAdd(true),
      disable: actionPerm(actionPermissions)?.add_perm,
    },

    {
      id: "2",
      name: "Download Data",
      image: isDownloadClicked ? downloadLoader : downloadIcon,
      actionHandler: isDownloadClicked
        ? () => {}
        : buttonConfig?.downloadHandler,
      disable: actionPerm(actionPermissions)?.download_perm,
    },
  ];

  const handleDownloadButtonClick = () => {
    console.log("Download button clicked!"); // Add this line
    buttonConfig.downloadHandler();
  };
  // let name = "Add Account";

  const action = actionPerm(actionPermissions);

  // if (!tableData) return <ComponentLoader />;

  return (
    <MasterTable
      name={name}
      columns={columns}
      tableData={tableFilter?.length > 0 ? tableFilter : tableData}
      buttonConfig={buttonConfig}
      setIsAdd={setIsAdd}
      setIsEdit={setIsEdit}
      setIsDelete={setIsDelete}
      setSelectedItemId={setSelectedItemId}
      loading={loading}
      renderKey={renderKey}
      actionConfig={actionConfig}
      filterField
      setGlobalFilters={setGlobalFilters}
      handleDownloadButtonClick={handleDownloadButtonClick}
      actions={action}
    />
  );
};

export default MasterLedgerTable;
